import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/TextInput';
import { validateWinnerName, WINNER_NAME_MAX } from '../winnerName';

/*
 * Propose the name of a draw winner.
 *
 * WHAT SUBMITTING DOES — and the dialog says so plainly, because it is not what
 * anyone would assume: the name is recorded IN THE PORTAL. The prize service is
 * untouched, the winner stays "à valider", and an operator copies the name into
 * the admin portal by hand. A dialog that looked like it approved the winner
 * would leave people thinking a job was finished when it had not started.
 */

export function WinnerNameDialog({
  drawDate,
  memberId,
  initialName,
  onSubmit,
  onClose,
}: {
  drawDate: string;
  memberId: string | null;
  initialName?: string | null;
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initialName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fieldWrap = useRef<HTMLDivElement>(null);

  // Focus the field on open: the dialog exists to be typed into, and a modal
  // that opens without focus makes a keyboard user hunt for it.
  // TextInput is a shared component that does not forward a ref, and it is not
  // worth changing its signature for one dialog — reach the input through the
  // wrapper instead.
  useEffect(() => {
    fieldWrap.current?.querySelector('input')?.focus();
  }, []);

  async function submit() {
    const checked = validateWinnerName(value);
    if ('error' in checked) {
      setError(checked.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(checked.name);
      onClose();
    } catch {
      setError('Le nom n’a pas pu être enregistré. Réessayez dans un instant.');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Renseigner le nom du gagnant"
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={submit} loading={saving} loadingText="Enregistrement…">
            Enregistrer
          </Button>
        </>
      }
    >
      <p className="mono-note" style={{ marginBottom: 14 }}>
        Tirage du {drawDate}
        {memberId ? ` · membre ${memberId}` : ''}
      </p>
      <div ref={fieldWrap}>
        <TextInput
          label="Nom du gagnant"
          hint="lettres, espaces, traits d’union et apostrophes"
          value={value}
          maxLength={WINNER_NAME_MAX}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          aria-invalid={error ? true : undefined}
        />
      </div>
      {error && (
        <p className="mono-note" role="alert" style={{ color: 'var(--mono-red)', marginTop: 8 }}>
          {error}
        </p>
      )}
      <p className="mono-foot" style={{ marginTop: 14 }}>
        Le nom est enregistré dans le portail pour être repris manuellement dans l’admin. Le service des lots n’est pas modifié et le gagnant reste « à valider
        ».
      </p>
    </Modal>
  );
}
